import {
  types,
  getParent,
  isStateTreeNode,
  type IAnyStateTreeNode,
  type IAnyType,
  type UnionOptions,
} from 'mobx-state-tree';
import DataType, { BUILTIN_DATA_TYPES, type IDataType } from './data_type';
import { isString } from 'lodash';
import { produce, type Draft } from 'immer';
import { modelReference } from '../refHelper';

// The mutable snapshot shape immer hands to preProcessSnapshot. It carries the
// full attribute creation snapshot (so the produced draft is assignable to the
// model's creation type); `type` is the serialized form — the builtin name or
// the referenced DataType id, both strings.
interface AttributeSnapshotDraft {
  id: string;
  name: string;
  type: string;
  description: string;
  implementation: string;
  validation: string;
}

/**
 * The Attribute instance interface (hand-declared so sibling views see each
 * other and the `owner`/`type` reads have concrete types).
 */
export interface IAttribute {
  readonly id: string;
  readonly name: string;
  readonly type: string | IDataType;
  readonly description: string;
  readonly implementation: string;
  readonly validation: string;
  asString(): string;
  readonly displayString: string;
  readonly owner: IAnyStateTreeNode;
  readonly hasBuiltinType: boolean;
  readonly typeName: string;
  setName(name: string): void;
  setType(type: string | IDataType): void;
  setDescription(description: string): void;
  setImplementation(implementation: string): void;
  setValidation(validation: string): void;
}

/**
 * A MERODE attribute
 */
const Attribute = types
  .model('Attribute', {
    id: types.identifier,
    name: types.string,
    // Type can be either the name of a builtin data type or a reference to a
    // custom data type. We don't use types.reference directly here, but our
    // custom wrapper (see '../refHelper.js' for the reason why).
    // A dispatcher-only union (no statically-listed member types) is valid at
    // runtime but not expressible in MST's `union` overloads, so the options
    // object is cast to the dispatcher-options shape MST expects.
    type: types.union({
      dispatcher: (snapshot: string) =>
        BUILTIN_DATA_TYPES.includes(snapshot)
          ? types.enumeration(BUILTIN_DATA_TYPES)
          : modelReference<typeof DataType>(DataType),
    } as UnionOptions<IAnyType[]>),
    description: types.string,
    implementation: types.string,
    validation: types.string,
  })
  .preProcessSnapshot(
    // The preProcessSnapshot hook should be a pure function that returns a new
    // snapshot. We use the produce utility from the immer library to do that
    // easily. See https://github.com/mweststrate/immer.
    produce((draft: Draft<AttributeSnapshotDraft>) => {
      // Make sure models that were created before we renamed DateTime to
      // Date/Time still work. !isStateTreeNode is needed because of
      // https://github.com/mobxjs/mobx-state-tree/issues/616
      // `as unknown` keeps the guard from narrowing `draft` to `never`.
      if (!isStateTreeNode(draft as unknown) && draft.type === 'DateTime') {
        draft.type = 'Date/Time';
      }
    })
  )
  .views((self0) => {
    const self = self0 as unknown as IAttribute;
    return {
    // Human-readable label, e.g. for `${attribute}` interpolation in messages.
    // (MST 7 forbids declaring a view named `toString`, so this is a plain view.)
    asString() {
      return `${self.name} (${self.owner})`;
    },
    get displayString() {
      return `Attribute ${self.asString()}`;
    },
    get owner(): IAnyStateTreeNode {
      // 2 levels up because 1 level up is the array of attributes.
      return getParent(self0, 2);
    },
    get hasBuiltinType() {
      // MST will automatically resolve the type to a reference to the custom
      // data type if it is of that type -> if it is a string, it is one of the
      // builtins.
      return isString(self.type);
    },
    // Get the name of the type without worrying about builtin vs custom
    get typeName(): string {
      if (self.hasBuiltinType) {
        return self.type as string;
      } else {
        return (self.type as IDataType).name;
      }
    },
    };
  })
  .actions((self) => ({
    setName(name: string) {
      self.name = name;
    },
    setType(type: string | IDataType) {
      self.type = type as typeof self.type;
    },
    setDescription(description: string) {
      self.description = description;
    },
    setImplementation(implementation: string) {
      self.implementation = implementation;
    },
    setValidation(validation: string) {
      self.validation = validation;
    },
  }));

export default Attribute;
